import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography, Box, Paper, Button, Tabs, Tab, Alert, CircularProgress } from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'
import {
  announcementService,
  Announcement,
  AnnouncementStatus,
} from '../services/announcementService'
import { AnnouncementTable, AnnouncementFormDialog } from '../components/announcement'

const STATUS_FILTERS: (AnnouncementStatus | 'all')[] = [
  'all',
  'DRAFT',
  'SCHEDULED',
  'PUBLISHED',
  'ARCHIVED',
]

const AnnouncementManagePage: React.FC = () => {
  const { t } = useTranslation('announcement')
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [tabValue, setTabValue] = useState(0)
  const [formOpen, setFormOpen] = useState(false)
  const [editAnnouncement, setEditAnnouncement] = useState<Announcement | null>(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  })

  const loadAnnouncements = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const status =
        STATUS_FILTERS[tabValue] === 'all'
          ? undefined
          : (STATUS_FILTERS[tabValue] as AnnouncementStatus)
      const result = await announcementService.getAnnouncements({
        status,
        page: pagination.page,
        limit: pagination.limit,
      })
      setAnnouncements(result.announcements)
      setPagination((prev) => ({
        ...prev,
        total: result.total,
        totalPages: result.totalPages,
      }))
    } catch (err: any) {
      setError(err.message || t('messages.loadError'))
    } finally {
      setLoading(false)
    }
  }, [tabValue, pagination.page, pagination.limit, t])

  useEffect(() => {
    loadAnnouncements()
  }, [loadAnnouncements])

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const handleCreate = () => {
    setEditAnnouncement(null)
    setFormOpen(true)
  }

  const handleEdit = (announcement: Announcement) => {
    setEditAnnouncement(announcement)
    setFormOpen(true)
  }

  const handleFormSubmit = () => {
    setFormOpen(false)
    loadAnnouncements()
  }

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }))
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('manage.title')}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          {t('manage.create')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label={t('status.all')} />
          <Tab label={t('status.draft')} />
          <Tab label={t('status.scheduled')} />
          <Tab label={t('status.published')} />
          <Tab label={t('status.archived')} />
        </Tabs>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <AnnouncementTable
          announcements={announcements}
          onEdit={handleEdit}
          onRefresh={loadAnnouncements}
          pagination={pagination}
          onPageChange={handlePageChange}
        />
      )}

      <AnnouncementFormDialog
        open={formOpen}
        announcement={editAnnouncement}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
      />
    </Box>
  )
}

export default AnnouncementManagePage
